/**
 * Delivery-option diagnostics.
 *
 * Enable in the browser with any of:
 *   ?delivery-debug=1 in the URL
 *   localStorage.setItem('deliveryDebug', '1')
 *   window.DELIVERY_DEBUG = true
 *
 * Logs why the delivery options in the cart drawer are or are not visible:
 * - Nationwide (PickEasy) is hidden unless every product is tagged "Nationwide",
 *   "DHL only" or "Christmas Preorders".
 * - Click & Collect (PickEasy) is always offered.
 * - Addison Lee is hidden unless every product is tagged "Addison Lee".
 * - A note explains a basket that mixes nationwide and London-only items.
 */
const DeliveryDebug = {
  get enabled() {
    try {
      return (
        window.DELIVERY_DEBUG === true ||
        new URLSearchParams(window.location.search).has('delivery-debug') ||
        localStorage.getItem('deliveryDebug') === '1'
      );
    } catch (error) {
      return window.DELIVERY_DEBUG === true;
    }
  },

  log(...args) {
    if (this.enabled) console.log('%c[delivery]', 'color:#a18d61;font-weight:bold', ...args);
  },

  group(label) {
    if (this.enabled) console.group(`%c[delivery] ${label}`, 'color:#a18d61;font-weight:bold');
  },

  groupEnd() {
    if (this.enabled) console.groupEnd();
  },

  table(rows) {
    if (this.enabled) console.table(rows);
  },

  // Reports the visibility of each fulfilment-type option and the reason for it.
  reportOptions(reason) {
    if (!this.enabled) return;

    const container = document.getElementById('ft-options-div');
    if (!container) {
      this.log('No #ft-options-div in the DOM. The PickEasy app has not rendered its options (yet).');
      return;
    }

    if (container.getAttribute('data-theme-dhl-only') === 'true') {
      this.log(`Whole delivery options section is hidden by the theme${reason ? ` (${reason})` : ''}.`);
      return;
    }

    const rows = [...container.querySelectorAll('.ft-options-cell')].map((cell) => {
      const label = cell.innerText.replace(/\s+/g, ' ').trim() || '(empty - app still rendering)';
      const inlineDisplay = cell.style.display;
      const computed = getComputedStyle(cell);
      const hiddenByTheme = inlineDisplay === 'none';
      const visible = computed.display !== 'none' && computed.visibility !== 'hidden' && cell.offsetParent !== null;
      const isNationwide = !!cell.querySelector('#standard-shipping-option-div');
      const isClickAndCollect = !!cell.querySelector('#pickup-option-div');
      const controlledBy = isNationwide || isClickAndCollect ? 'theme + app' : 'app only';

      let why;
      if (visible) {
        if (isNationwide) {
          why = 'Shown: theme left it alone (all cart products are nationwide-eligible)';
        } else if (isClickAndCollect) {
          why = 'Shown: Click & Collect is always offered (every product can be collected)';
        } else {
          why = 'Shown: rendered by the PickEasy app, the theme never touches this option';
        }
      } else if (hiddenByTheme && isNationwide) {
        why = `Hidden by the theme (display:none set in cart-drawer.js) - ${reason || 'a product has no "Nationwide", "DHL only" or "Christmas Preorders" tag'}`;
      } else {
        why = 'Hidden by the PickEasy app or by CSS, not by the theme';
      }

      return { option: label, visible, hiddenByTheme, controlledBy, why };
    });

    this.group(`option visibility (${rows.length} found)`);
    this.table(rows);
    rows.forEach((row) => this.log(`${row.visible ? 'VISIBLE' : 'HIDDEN '} | ${row.option} | ${row.why}`));
    this.groupEnd();
  },
};

class CartDrawer extends HTMLElement {
  // How long to wait for the app to render its delivery options before giving up.
  static STANDARD_SHIPPING_TIMEOUT_MS = 8000;

  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Re-check the delivery rules whenever the basket changes, now that the page no
    // longer reloads after adding or removing items (see the reload switches in
    // cart.js and global.js). The theme publishes "cartUpdate" after every change.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdated(event));
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.setDrawerFooterLoading(true);
    DeliveryDebug.log('Drawer opened. Watching for the app to render the delivery options.');
    this.syncStandardShippingOption();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  // Kept public: cart-click-and-collect.js used to call this (that script is no longer
  // loaded since 26 Oct 2026, but any other caller still works).
  // (The name is historical; it re-runs all the delivery rules, not just "DHL only".)
  async toggleStandardShippingOptionForDhlOnly() {
    const eligibility = await this.getCartDeliveryEligibility();
    this.applyDeliveryOptionVisibility(eligibility);
    DeliveryDebug.reportOptions(this.lastEligibilityReason);
  }

  /**
   * Decides whether PickEasy delivery options should be visible.
   *
   * The options are rendered by the PickEasy app, so the theme cannot know when they
   * exist. Rather than guessing with a fixed delay, the tag check and the wait
   * for the elements run concurrently, and the result is applied as soon as both
   * are ready. A MutationObserver then keeps the decision applied if the app
   * re-renders its options.
   *
   * Nationwide is hidden unless every product can go nationwide. Click & Collect
   * is always offered, as every product can be collected. A basket mixing nationwide
   * and London-only items gets a note explaining why Nationwide isn't offered.
   */
  /**
   * Runs after any basket change (remove, quantity change, add).
   *
   * Adds from a product page or the quick view reopen the drawer, which already runs
   * the checks, so those are skipped here to avoid checking twice. For everything
   * else (mainly removing items or changing quantities in the drawer), the old
   * options watcher is dropped (the drawer has just been redrawn, so it may be
   * watching elements that no longer exist) and the full check runs again.
   *
   * Read-only: the check reads the basket and products, it never writes to the
   * basket, so it cannot set off a request loop.
   */
  onCartUpdated(event) {
    // The basket has been redrawn by now (cart.js sends this message afterwards),
    // so put the Addison Lee button back first. Adds go through renderContents,
    // which does the same, so doing it here too is harmless.
    this.restoreCourierButton();

    if (event && (event.source === 'product-form' || event.source === 'quick-view')) return;

    if (this.standardShippingObserver) {
      this.standardShippingObserver.disconnect();
      this.standardShippingObserver = null;
    }

    DeliveryDebug.log('Basket changed. Re-checking the delivery rules.');
    this.syncStandardShippingOption();
  }

  async syncStandardShippingOption() {
    const eligibilityPromise = this.getCartDeliveryEligibility();

    // Addison Lee is a separate widget, not part of the PickEasy options, so there is
    // no need to wait for PickEasy before hiding it.
    // The mixed basket note and the Addison Lee rule don't depend on PickEasy either,
    // so apply them as soon as the tag check finishes. (Both are also re-applied
    // below once PickEasy has drawn its options.)
    eligibilityPromise.then((eligibility) => {
      this.applyCourierAvailability(eligibility);
      this.applyMixedBasketNote(eligibility);
    });

    // PickEasy draws nothing for an empty basket, so stop waiting for it as soon as
    // the check says the basket is empty (otherwise it waits the full timeout).
    const stopWaitingIfEmpty = eligibilityPromise.then((eligibility) =>
      eligibility && eligibility.isEmpty ? true : new Promise(() => {})
    );

    try {
      const [optionsContainer, eligibility] = await Promise.all([
        this.waitForDeliveryOption('#ft-options-div', 'delivery options', stopWaitingIfEmpty),
        eligibilityPromise,
      ]);

      if (!optionsContainer && eligibility && eligibility.isEmpty) {
        DeliveryDebug.log('The basket is empty, so there are no delivery options to wait for.');
        return;
      }

      if (!optionsContainer) {
        DeliveryDebug.log(
          `The app never rendered #ft-options-div within ${CartDrawer.STANDARD_SHIPPING_TIMEOUT_MS}ms,`,
          'so the theme could not show or hide delivery options.'
        );
        return;
      }

      this.applyDeliveryOptionVisibility(eligibility);
      this.observeDeliveryOptions();
    } finally {
      this.setDrawerFooterLoading(false);
      DeliveryDebug.reportOptions(this.lastEligibilityReason);
    }
  }

  // Resolves with the option element as soon as the app renders it, or null on timeout.
  // Optional stopWaiting: a promise that, when it resolves, ends the wait early (null).
  waitForDeliveryOption(selector, label, stopWaiting) {
    const existing = document.querySelector(selector);
    if (existing) {
      DeliveryDebug.log(`The app had already rendered the ${label}, no waiting needed.`);
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();
      let settled = false;

      const finish = (element) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timeoutId);
        if (element) DeliveryDebug.log(`The app rendered the ${label} after ${Date.now() - startedAt}ms.`);
        resolve(element);
      };

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) finish(element);
      });

      const timeoutId = setTimeout(() => finish(null), CartDrawer.STANDARD_SHIPPING_TIMEOUT_MS);

      observer.observe(document.body, { childList: true, subtree: true });
      if (stopWaiting) stopWaiting.then(() => finish(null));
    });
  }

  // Backwards-compatible alias used by older call sites.
  waitForStandardShippingOption() {
    return this.waitForDeliveryOption('#standard-shipping-option-div', 'Nationwide option');
  }

  // Re-applies the decision if the app replaces its options while the drawer is open.
  observeDeliveryOptions() {
    const container = document.getElementById('ft-options-div');
    if (!container || this.standardShippingObserver) return;

    this.standardShippingObserver = new MutationObserver(() => {
      if (!this.lastDeliveryEligibility || this.applyingDeliveryVisibility) return;

      // The whole section is never hidden any more ("DHL only" baskets can be collected).
      const shouldHideSection = false;
      const sectionHidden = container.getAttribute('data-theme-dhl-only') === 'true';

      if (shouldHideSection === sectionHidden && shouldHideSection) return;

      if (!shouldHideSection) {
        const nationwideCell = document.getElementById('standard-shipping-option-div')?.closest('.ft-options-cell');
        const shouldHideNationwide = !this.lastDeliveryEligibility.allNationwideEligible;
        const nationwideMatches =
          !nationwideCell || (nationwideCell.style.display === 'none') === shouldHideNationwide;
        if (nationwideMatches && !sectionHidden) return;
      }

      DeliveryDebug.log('The app re-rendered its options, re-applying the theme decision.');
      this.applyDeliveryOptionVisibility(this.lastDeliveryEligibility);
    });

    this.standardShippingObserver.observe(container.parentElement || container, { childList: true, subtree: true });
  }

  // Backwards-compatible alias.
  observeStandardShippingOption() {
    this.observeDeliveryOptions();
  }

  async getCartDeliveryEligibility() {
    this.lastEligibilityReason = null;

    const emptyResult = {
      allNationwideEligible: false,
      allCourierEligible: false,
      isMixedBasket: false,
      isEmpty: false,
      nationwideTitles: [],
      londonOnlyTitles: [],
    };

    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        this.lastEligibilityReason = `could not read /cart.js (HTTP ${cartResponse.status}), so the check failed closed`;
        DeliveryDebug.log('FAIL:', this.lastEligibilityReason);
        return emptyResult;
      }
      const cart = await cartResponse.json();
      const handles = [...new Set((cart.items || []).map((item) => item.handle).filter(Boolean))];

      if (!handles.length) {
        this.lastEligibilityReason = 'the cart is empty, and an empty cart is treated as not eligible';
        DeliveryDebug.log('Cart is empty ->', this.lastEligibilityReason);
        return { ...emptyResult, isEmpty: true };
      }

      const productChecks = await Promise.all(
        handles.map(async (handle) => {
          try {
            const productResponse = await fetch(`/products/${encodeURIComponent(handle)}.js`);
            if (!productResponse.ok) {
              DeliveryDebug.log(`${handle}: HTTP ${productResponse.status} from /products/${handle}.js, counted as NOT eligible`);
              return { nationwideEligible: false, courierEligible: false, title: handle };
            }
            const product = await productResponse.json();

            const tags = Array.isArray(product.tags) ? product.tags : [];
            const normalizedTags = tags.map((tag) => String(tag).toLowerCase());
            const dhlOnly = normalizedTags.includes('dhl only');
            const christmasPreorder = normalizedTags.includes('christmas preorders');
            const nationwideEligible =
              dhlOnly ||
              normalizedTags.includes('nationwide') ||
              christmasPreorder;
            // London courier (Addison Lee) is opt-in: only products tagged "Addison Lee".
            // Same tag the "Local Delivery" badge uses (snippets/delivery-badges.liquid).
            const courierEligible = normalizedTags.includes('addison lee');
            const matchedTag = tags.find((tag) => {
              const normalizedTag = String(tag).toLowerCase();
              return (
                normalizedTag === 'dhl only' ||
                normalizedTag === 'nationwide' ||
                normalizedTag === 'christmas preorders'
              );
            });

            DeliveryDebug.log(
              `${handle}: ${matchedTag ? `nationwide-eligible via tag "${matchedTag}"` : 'NOT nationwide-eligible'}${
                dhlOnly ? ' (DHL only)' : ''
              }${christmasPreorder ? ' (Christmas preorder)' : ''}; ${
                courierEligible ? 'London courier allowed (tag "Addison Lee")' : 'NO London courier (no "Addison Lee" tag)'
              }`,
              `| tags: [${tags.join(', ')}]`
            );

            return { nationwideEligible, courierEligible, title: product.title || handle };
          } catch (error) {
            DeliveryDebug.log(`${handle}: request failed (${error.message}), counted as NOT eligible`);
            return { nationwideEligible: false, courierEligible: false, title: handle };
          }
        })
      );

      const allNationwideEligible = productChecks.every((check) => check.nationwideEligible);
      // Addison Lee is offered only when every item may go by London courier.
      const allCourierEligible = productChecks.every((check) => check.courierEligible);
      // Some items can be sent nationwide and some can't: they can still be collected
      // together, but Nationwide is hidden, so the note explains why.
      // (Before 20 Oct 2026 this only looked at the legacy "DHL only" tag.)
      const nationwideTitles = productChecks.filter((check) => check.nationwideEligible).map((check) => check.title);
      const londonOnlyTitles = productChecks.filter((check) => !check.nationwideEligible).map((check) => check.title);
      // The note only appears when Click & Collect is the only option left. If every
      // item can go by Addison Lee, the customer has local delivery as well, so a
      // "Nationwide isn't available" warning would just be noise (agreed 21 Oct 2026).
      const isMixedBasket = nationwideTitles.length > 0 && londonOnlyTitles.length > 0 && !allCourierEligible;
      const noCourierHandles = handles.filter((handle, index) => !productChecks[index].courierEligible);

      this.lastEligibilityReason = [
        allNationwideEligible
          ? `every cart product is nationwide-eligible via "Nationwide", "DHL only", or "Christmas preorders" (${handles.join(', ')})`
          : `these cart products are not nationwide-eligible: ${londonOnlyTitles.join(', ')}`,
        allCourierEligible
          ? 'Addison Lee shown: every product is tagged "Addison Lee"'
          : `Addison Lee hidden: not tagged "Addison Lee": ${noCourierHandles.join(', ')}`,
      ].join('; ');

      DeliveryDebug.log(
        `Verdict: all ${handles.length} product(s) nationwide-eligible = ${allNationwideEligible}; all London courier = ${allCourierEligible}.`,
        this.lastEligibilityReason
      );

      if (isMixedBasket) {
        DeliveryDebug.log(
          `Mixed basket: nationwide (${nationwideTitles.join(', ')}) with London only (${londonOnlyTitles.join(', ')}). Nationwide hidden, note shown.`
        );
      }

      return { allNationwideEligible, allCourierEligible, isMixedBasket, nationwideTitles, londonOnlyTitles };
    } catch (error) {
      this.lastEligibilityReason = `the eligibility check threw (${error.message}), so it failed closed`;
      DeliveryDebug.log('ERROR:', this.lastEligibilityReason);
      return emptyResult;
    }
  }

  // Backwards-compatible wrapper around the shared eligibility check.
  async cartHasOnlyNationwideEligibleProducts() {
    const eligibility = await this.getCartDeliveryEligibility();
    return eligibility.allNationwideEligible;
  }

  applyDeliveryOptionVisibility(eligibility) {
    this.lastDeliveryEligibility = eligibility;
    this.applyingDeliveryVisibility = true;

    try {
      this.applyMixedBasketNote(eligibility);
      this.applyCourierAvailability(eligibility);

      // Every product can be collected, so the section is always shown. This call also
      // clears any hiding on the app's slot and location elements, which Click & Collect needs.
      this.applyDeliverySectionVisibility(false);
      this.applyOptionCellVisibility(
        '#standard-shipping-option-div',
        !eligibility.allNationwideEligible,
        'Nationwide'
      );
      this.applyOptionCellVisibility('#pickup-option-div', false, 'Click & Collect');
    } finally {
      this.applyingDeliveryVisibility = false;
    }
  }

  applyDeliverySectionVisibility(shouldHide) {
    const selectors = [
      '#ft-options-div',
      '#orders-slots-heading',
      '#orders-slots-div',
      '.order-delivery-locations',
      '.order-slots-error',
      '.pickup-location',
    ];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (shouldHide) {
          element.style.setProperty('display', 'none', 'important');
          if (element.id === 'ft-options-div') {
            element.setAttribute('data-theme-dhl-only', 'true');
          }
        } else {
          element.style.removeProperty('display');
          if (element.id === 'ft-options-div') {
            element.removeAttribute('data-theme-dhl-only');
          }
        }
      });
    });

    // Restore per-cell displays when showing the section again.
    if (!shouldHide) {
      document.querySelectorAll('#ft-options-div .ft-options-cell').forEach((cell) => {
        cell.style.removeProperty('display');
      });
    }

    DeliveryDebug.log(
      shouldHide
        ? 'Hiding the whole PickEasy delivery options section.'
        : 'Showing the PickEasy delivery options section.'
    );
  }

  applyOptionCellVisibility(selector, shouldHide, label) {
    const option = document.querySelector(selector);
    if (!option) {
      DeliveryDebug.log(`${selector} is not in the DOM, so the ${label} option could not be updated.`);
      return;
    }

    const optionCell = option.closest('.ft-options-cell');
    if (!optionCell) {
      DeliveryDebug.log(`Found ${selector} but it has no .ft-options-cell ancestor, so nothing was changed.`);
      return;
    }

    DeliveryDebug.log(`${shouldHide ? 'Hiding' : 'Showing'} the ${label} option.`);

    if (shouldHide) {
      optionCell.style.setProperty('display', 'none', 'important');
    } else {
      optionCell.style.removeProperty('display');
    }
  }

  applyStandardShippingOptionVisibility(shouldHide) {
    this.lastShouldHideStandardShipping = shouldHide;
    this.applyOptionCellVisibility('#standard-shipping-option-div', shouldHide, 'Nationwide');
  }

  // Backwards-compatible alias for any app or script still calling the old name.
  setStandardShippingOptionVisibility(shouldHide) {
    this.applyStandardShippingOptionVisibility(shouldHide);
  }

  /**
   * Addison Lee (London courier) availability.
   *
   * Opt-in by tag (20 Oct 2026): Addison Lee is offered only when every item in the
   * basket is tagged "Addison Lee" (the tag the "Local Delivery" badge uses). If any
   * item isn't, or the check fails, the class "cart-drawer--no-courier" is added to
   * the drawer; it is removed otherwise. An untagged product therefore hides the
   * courier rather than allowing a delivery that can't be fulfilled. (Previously it
   * was hidden only for the legacy "DHL only" tag.) A stylesheet rule in component-cart-drawer.css hides the Addison Lee
   * widget while the class is present, and keeps doing so if the widget redraws.
   *
   * This only toggles a class. It deliberately sends no requests to Shopify: an
   * earlier version also cleared the Addison Lee booking from the cart attributes,
   * and that set off a request loop between the apps on the page (6 Oct 2026).
   * Safe to call any number of times.
   */
  applyCourierAvailability(eligibility) {
    this.classList.toggle('cart-drawer--no-courier', !eligibility || !eligibility.allCourierEligible);
  }

  /**
   * Put the Addison Lee button back after the basket is redrawn.
   *
   * The Addison Lee app inserts its "Addison Lee delivery" button into the basket
   * footer once, when the page loads. Redrawing the basket (add, remove, quantity
   * change, quick view) replaces the footer and removes the button. The app does
   * watch for this, but its watcher switches itself off after the first change
   * ("disconnect after first change" in their pre-purchase-form.js), so it never
   * comes back. The page reloads used to hide the problem.
   *
   * The app keeps hold of its button and has its own function, handleDOMChange(),
   * which puts that same button back (keeping its event listeners) and refreshes
   * it to show any existing booking. We simply call it.
   *
   * - If the Addison Lee embed is switched off, or Addison Lee rename the function
   *   in an update, this quietly does nothing (the button just won't come back
   *   until a page reload, which is how it behaved before).
   * - handleDOMChange() also re-reads the basket and, if a booking exists but the
   *   item it was stored on was removed, moves it to another item. The app already
   *   did this on every page load. It doesn't send the theme's "basket updated"
   *   message, so it can't set off a request loop with onCartUpdated.
   * - The "Addison Lee" tag rule (applyCourierAvailability + CSS) still applies.
   * Safe to call any number of times.
   */
  restoreCourierButton() {
    const form = document.querySelector('pre-purchase-form');
    if (!form || typeof form.handleDOMChange !== 'function') return;
    if (this.querySelector('.pre-purchase-modal__update-location-in-dom')) return; // already there

    DeliveryDebug.log('Putting the Addison Lee button back after the basket redraw.');
    try {
      const result = form.handleDOMChange(); // async in the app, so catch both ways
      if (result && typeof result.catch === 'function') {
        result.catch((error) => DeliveryDebug.log('Could not put the Addison Lee button back.', error));
      }
    } catch (error) {
      DeliveryDebug.log('Could not put the Addison Lee button back.', error);
    }
  }

  /**
   * Basket mixes items that can be sent nationwide with items that can't. Nationwide is hidden (by
   * applyOptionCellVisibility), so this explains why, names the items, and points to
   * collection or a separate order. Checkout is not blocked: everything can be collected.
   * Only changes the page when something actually differs, so it is safe to call
   * repeatedly (the options observer calls it on every app redraw).
   */
  applyMixedBasketNote(eligibility) {
    const existing = this.querySelector('.cart-drawer__delivery-note');

    if (!eligibility || !eligibility.isMixedBasket) {
      if (existing) existing.remove();
      return;
    }

    const key = JSON.stringify([eligibility.nationwideTitles, eligibility.londonOnlyTitles]);
    if (existing && existing.dataset.key === key) return;
    if (existing) existing.remove();

    const ctas = this.querySelector('.cart__ctas');
    if (!ctas) return;

    const list = (titles) =>
      titles.length < 2 ? titles.join('') : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;

    const note = document.createElement('div');
    note.className = 'cart-drawer__delivery-note';
    note.setAttribute('role', 'status');
    note.dataset.key = key;

    const heading = document.createElement('p');
    heading.className = 'cart-drawer__delivery-note-heading';
    heading.textContent = "Nationwide delivery isn't available";

    const detail = document.createElement('p');
    // Kept short so it doesn't crowd the basket on phones (tidied 20 Oct 2026).
    detail.textContent =
      `${list(eligibility.londonOnlyTitles)} can't be sent nationwide. ` +
      `Collect everything from the shop, or order ${list(eligibility.nationwideTitles)} separately for nationwide delivery.`;

    note.append(heading, detail);
    ctas.before(note);
  }

  setDrawerFooterLoading(isLoading) {
    const drawerFooter = this.querySelector('.drawer__footer');
    if (!drawerFooter) return;

    let loader = drawerFooter.querySelector('.drawer-footer-loader');

    if (isLoading) {
      if (!loader) {
        loader = document.createElement('div');
        loader.className = 'drawer-footer-loader';
        loader.setAttribute('role', 'status');
        loader.setAttribute('aria-live', 'polite');
        loader.innerHTML = `
          <div class="drawer-footer-loader__inner">
            <span class="drawer-footer-loader__spinner" aria-hidden="true"></span>
            <span class="drawer-footer-loader__text">Checking delivery options...</span>
          </div>
        `;
        if (getComputedStyle(drawerFooter).position === 'static') {
          drawerFooter.style.position = 'relative';
        }
        drawerFooter.appendChild(loader);
      }
      return;
    }

    if (loader) loader.remove();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    if (this.standardShippingObserver) {
      this.standardShippingObserver.disconnect();
      this.standardShippingObserver = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.restoreCourierButton(); // the redraw above removed the Addison Lee button
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

}

customElements.define('cart-drawer-items', CartDrawerItems);
